import React, { createContext, useEffect, useState } from 'react'
import menu, { docsMenu } from '../../navs'
import { IMenu } from 'components/PostLayout/types'
import { useLocation } from '@reach/router'
import { navigate } from 'gatsby'
import { isSafeInternalPath } from 'lib/internalPath'
import { useActions } from 'kea'
import { layoutLogic } from 'logic/layoutLogic'

export const Context = createContext<any>(undefined)

function recursiveSearch(array, value) {
    for (let i = 0; i < array?.length || 0; i++) {
        const element = array[i]

        if (typeof element === 'string' && element.split('?')[0] === value) {
            return true
        }

        if (typeof element === 'object' && element !== null) {
            const found = recursiveSearch(Object.values(element), value)
            if (found) {
                return true
            }
        }
    }

    return false
}

export interface IProps {
    children: React.ReactNode
    parent?: IMenu
    activeInternalMenu?: IMenu
}

export const LayoutProvider = ({ children, ...other }: IProps) => {
    const { pathname, search } = useLocation()
    const { setWebsiteTheme } = useActions(layoutLogic)
    const [compact, setCompact] = useState(false)
    const [fullWidthContent, setFullWidthContent] = useState<boolean>(false)
    const [hedgehogModeEnabled, _setHedgehogModeEnabled] = useState<boolean>(false)

    // Hydrate client-only state after mount
    useEffect(() => {
        const compactValue = window !== window.parent
        setCompact(compactValue)
        setFullWidthContent(compactValue || localStorage.getItem('full-width-content') === 'true')

        const today = new Date()
        const isAprilFirst = today.getMonth() === 3 && today.getDate() === 1
        let hedgehogMode = localStorage.getItem('hedgehog-mode-enabled')
        if (isAprilFirst && typeof hedgehogMode !== 'string') {
            hedgehogMode = 'true'
        }
        _setHedgehogModeEnabled(hedgehogMode === 'true')
    }, [])

    const [enterpriseMode, setEnterpriseMode] = useState(false)
    const [theoMode, setTheoMode] = useState(false)
    const [post, setPost] = useState<boolean>(false)
    const parent =
        other.parent ??
        menu.find(({ children, url }) => {
            const currentURL = pathname
            return currentURL === url.split('?')[0] || recursiveSearch(children, currentURL)
        })

    const internalMenu = parent?.children

    const activeInternalMenu =
        other.activeInternalMenu ??
        internalMenu?.find((menuItem) => {
            const currentURL = pathname
            return currentURL === menuItem.url?.split('?')[0] || recursiveSearch(menuItem.children, currentURL)
        })

    useEffect(() => {
        localStorage.setItem('full-width-content', fullWidthContent + '')
    }, [fullWidthContent])

    const setHedgehogModeEnabled = (enabled: boolean) => {
        _setHedgehogModeEnabled(enabled)
        localStorage.setItem('hedgehog-mode-enabled', enabled + '')
    }

    useEffect(() => {
        if (compact) {
            // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration - intentional for docs embedding, parent origin unknown, non-sensitive navigation data
            window.parent.postMessage(
                {
                    type: 'internal-navigation',
                    url: pathname,
                },
                '*'
            )
        }
    }, [pathname])

    useEffect(() => {
        if (compact) {
            // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration - intentional for docs embedding, parent origin unknown, non-sensitive menu data
            window.parent.postMessage(
                {
                    type: 'docs-active-menu',
                    activeMenuName: activeInternalMenu?.name,
                },
                '*'
            )
        }
    }, [activeInternalMenu])

    useEffect(() => {
        if (window) {
            setWebsiteTheme(window.__theme)
            window.__onThemeChange = () => {
                setWebsiteTheme(window.__theme)
            }
        }
        if (compact) {
            // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration - intentional for docs embedding, parent origin unknown, non-sensitive ready signal
            window.parent.postMessage(
                {
                    type: 'docs-ready',
                },
                '*'
            )

            // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration - intentional for docs embedding, parent origin unknown, non-sensitive menu data
            window.parent.postMessage(
                {
                    type: 'docs-menu',
                    menu: docsMenu.children,
                },
                '*'
            )
        }

        const onMessage = (e: MessageEvent): void => {
            if (e.data.type === 'theme-toggle') {
                window.__setPreferredTheme(e.data.isDarkModeOn ? 'dark' : 'light')
                return
            }
            if (e.data.type === 'navigate' && isSafeInternalPath(e.data.url)) {
                navigate(e.data.url)
            }
        }

        window.addEventListener('message', onMessage)

        return () => window.removeEventListener('message', onMessage)
    }, [])

    useEffect(() => {
        if (enterpriseMode) {
            document.querySelector('body')?.setAttribute('style', 'font-family: Verdana !important')
        } else {
            document.querySelector('body')?.removeAttribute('style')
        }
    }, [enterpriseMode])

    useEffect(() => {
        if (pathname !== '/') {
            setEnterpriseMode(false)
        }
        if (pathname === '/' && search.includes('synergy=true')) {
            setEnterpriseMode(true)
        }
        if (
            ['/blog/', '/founders/', '/product-engineers/', '/newsletter/'].some((prefix) =>
                pathname.startsWith(prefix)
            )
        ) {
            setPost(true)
        } else {
            setPost(false)
            setTheoMode(false)
        }
    }, [pathname])

    return (
        <Context.Provider
            value={{
                menu,
                parent,
                internalMenu,
                activeInternalMenu,
                fullWidthContent,
                setFullWidthContent,
                compact,
                enterpriseMode,
                setEnterpriseMode,
                theoMode,
                setTheoMode,
                post,
                hedgehogModeEnabled,
                setHedgehogModeEnabled,
            }}
        >
            {children}
        </Context.Provider>
    )
}
